import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { resolveImageUrl } from '../utils/api';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

// Customer-visible wording for each stage of the manual payment workflow.
const STAGES = [
    { key: 'awaiting_payment', label: 'Awaiting payment', blurb: 'Transfer the total and send us the receipt on WhatsApp.' },
    { key: 'payment_review', label: 'Verifying payment', blurb: "We're checking your transfer. This is usually quick." },
    { key: 'paid', label: 'Payment confirmed', blurb: 'Your receipt is ready and we are packing your order.' },
    { key: 'packed', label: 'Packed', blurb: 'Your order is packed and awaiting collection by the courier.' },
    { key: 'shipped', label: 'Shipped', blurb: 'On its way — track it with the number below.' },
    { key: 'delivered', label: 'Delivered', blurb: 'Delivered. Thank you for shopping with us!' },
];

const money = (amount) => `RM ${Number(amount || 0).toFixed(2)}`;

// Live status polling. Once an order is delivered or cancelled nothing further
// happens, so polling stops rather than hitting the server forever on a page
// someone left open.
const POLL_INTERVAL_MS = 15000;
const TERMINAL_STATUSES = ['delivered', 'cancelled'];

// The page headline follows the order's stage, so it always states the next
// thing that matters to the customer rather than a fixed instruction.
const HEADLINES = {
    awaiting_payment: {
        title: 'Please complete the payment',
        subtitle: 'Your order is reserved. Transfer the total and send us the receipt on WhatsApp.',
    },
    payment_review: {
        title: 'Checking your payment',
        subtitle: "Thanks — we're verifying your transfer and will confirm shortly.",
    },
    paid: {
        title: 'Payment received',
        subtitle: "We're packing your order now.",
    },
    packed: {
        title: 'Your order is packed',
        subtitle: "It's ready for the courier and will ship shortly.",
    },
    shipped: {
        title: 'Your order is on its way',
        subtitle: 'Track it with the details below.',
    },
    delivered: {
        title: 'Delivered',
        subtitle: 'Thank you for shopping with us!',
    },
    cancelled: {
        title: 'Order cancelled',
        subtitle: 'This order was cancelled. Get in touch if that looks wrong.',
    },
};

const OrderStatusPage = () => {
    const { token } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [reporting, setReporting] = useState(false);
    const [copied, setCopied] = useState('');
    const [justAdvanced, setJustAdvanced] = useState(null);
    const copyTimer = useRef(null);

    useEffect(() => () => clearTimeout(copyTimer.current), []);

    const copyToClipboard = async (text, label) => {
        let ok = false;
        try {
            // Only available over HTTPS (or localhost); falls through otherwise.
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                ok = true;
            }
        } catch {
            ok = false;
        }

        if (!ok) {
            // Legacy path so the button still works on a plain-HTTP origin.
            try {
                const field = document.createElement('textarea');
                field.value = text;
                field.setAttribute('readonly', '');
                field.style.position = 'fixed';
                field.style.opacity = '0';
                document.body.appendChild(field);
                field.select();
                ok = document.execCommand('copy');
                document.body.removeChild(field);
            } catch {
                ok = false;
            }
        }

        if (!ok) return; // stay silent rather than claim a copy that did not happen
        setCopied(label);
        clearTimeout(copyTimer.current);
        copyTimer.current = setTimeout(() => setCopied(''), 2000);
    };

    const load = useCallback(async ({ silent = false } = {}) => {
        try {
            const res = await fetch(`${API_BASE}/orders/public/${token}`);
            if (!res.ok) throw new Error('We could not find that order.');
            const next = await res.json();
            setOrder(prev => {
                // Flag a real transition so the page can draw attention to it.
                if (prev && prev.status !== next.status) setJustAdvanced(next.status);
                return next;
            });
        } catch (err) {
            // A failed background poll must not replace a page the customer is
            // reading with an error — offline or a blip, we simply try again.
            if (!silent) setError(err.message);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const orderStatus = order?.status;

    // Keep the page live so the customer sees payment confirmation and tracking
    // without refreshing. Polling rather than sockets: updates come from a human
    // in the admin panel, so they are minutes apart at best.
    useEffect(() => {
        if (!orderStatus || TERMINAL_STATUSES.includes(orderStatus)) return; // nothing left to change

        let timer;
        const tick = () => {
            // Skip work for a backgrounded tab; the visibility handler below
            // catches up the moment the customer looks again.
            if (!document.hidden) load({ silent: true });
            timer = setTimeout(tick, POLL_INTERVAL_MS);
        };
        timer = setTimeout(tick, POLL_INTERVAL_MS);

        const onVisibility = () => { if (!document.hidden) load({ silent: true }); };
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('visibilitychange', onVisibility);
        };
        // Keyed on status alone, not the whole order: re-running on every poll
        // would restart the timer continuously and never settle.
    }, [orderStatus, load]);

    // Clear the "just advanced" highlight after it has been noticed.
    useEffect(() => {
        if (!justAdvanced) return;
        const t = setTimeout(() => setJustAdvanced(null), 6000);
        return () => clearTimeout(t);
    }, [justAdvanced]);

    const reportPaymentSent = async () => {
        setReporting(true);
        try {
            await fetch(`${API_BASE}/orders/public/${token}/payment-sent`, { method: 'POST' });
            await load();
        } finally {
            setReporting(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center font-light text-gray-500">Loading your order…</div>;
    }

    if (error || !order) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center">
                <h1 className="text-2xl font-light">{error || 'Order not found'}</h1>
                <Link to="/catalog" className="underline font-light">Back to the store</Link>
            </div>
        );
    }

    const isCancelled = order.status === 'cancelled';
    const currentStage = STAGES.findIndex(s => s.key === order.status);
    const payment = order.payment;
    const headline = HEADLINES[order.status] || HEADLINES.awaiting_payment;
    const isLive = !TERMINAL_STATUSES.includes(order.status);

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Slim header — the site nav is hidden on this page, so this is the
                way back to the store. */}
            <header className="border-b border-gray-200 bg-white sticky top-0 z-30">
                <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between">
                    <Link to="/catalog" className="text-sm font-light text-gray-600 hover:text-black transition-colors">
                        ← Continue shopping
                    </Link>
                    <span className="text-lg font-light tracking-tight text-black">Doctape</span>
                    <span className="w-28 hidden sm:block" aria-hidden="true" />
                </div>
            </header>

            <div className="max-w-2xl mx-auto space-y-6 pt-8 pb-16 px-4">

                {/* Headline states what the customer must do next. It tracks
                    status deliberately: leaving "complete the payment" up after
                    an order has shipped would read as a demand to pay twice.
                    Order meta sits here rather than in its own card — as a
                    separate panel it repeated the total for no benefit. */}
                <header className="text-center pt-4 pb-2">
                    <h1 className="text-4xl sm:text-5xl font-light tracking-tight leading-tight">
                        {headline.title}
                    </h1>
                    <p className="text-gray-500 font-light mt-3 max-w-md mx-auto">{headline.subtitle}</p>

                    <p className="text-sm text-gray-500 font-light mt-6">
                        <span className="font-medium text-black">{order.orderNumber}</span>
                        <span className="mx-2 text-gray-300">·</span>
                        {new Date(order.createdAt).toLocaleDateString()}
                        <span className="mx-2 text-gray-300">·</span>
                        {money(order.totalPrice)}
                    </p>
                    <p className="text-xs text-gray-400 font-light mt-2">
                        Bookmark this page — it is your live order status and receipt.
                    </p>
                </header>

                {/* Payment instructions, shown only while payment is outstanding */}
                {payment && (
                    <div className="bg-white border border-black p-6 sm:p-8">
                        <div className="flex items-baseline justify-between gap-4 mb-6 pb-4 border-b border-gray-100">
                            <h2 className="text-xl font-light tracking-tight">How to pay</h2>
                            <span className="text-2xl font-light tracking-tight whitespace-nowrap">{money(order.totalPrice)}</span>
                        </div>

                        {payment.configured ? (
                            <>
                                {/* Two ways to pay, each given its own full-width
                                    panel. Side by side they fought for room: the
                                    QR is a tall poster, not a bare code. */}
                                <p className="text-xs tracking-widest text-gray-500 font-light mb-3">BANK TRANSFER</p>
                                <dl className="border-t border-gray-200 divide-y divide-gray-100">
                                    {[
                                        { label: 'Bank', value: payment.bank.bankName },
                                        { label: 'Account name', value: payment.bank.accountName },
                                        {
                                            label: 'Account number',
                                            value: payment.bank.accountNumber,
                                            // Copy the digits alone — banking apps
                                            // often reject a pasted value with spaces.
                                            copyValue: String(payment.bank.accountNumber || '').replace(/\s/g, ''),
                                        },
                                        { label: 'Amount', value: money(order.totalPrice), copyValue: Number(order.totalPrice || 0).toFixed(2) },
                                        { label: 'Reference', value: order.orderNumber, copyValue: order.orderNumber },
                                    ].map(({ label, value, copyValue }) => value ? (
                                        <div key={label} className="flex justify-between items-center py-3 gap-4">
                                            <dt className="text-gray-500 font-light text-sm flex-shrink-0">{label}</dt>
                                            <dd className="font-medium text-sm text-right break-all flex items-center gap-2 justify-end">
                                                <span>{value}</span>
                                                {copyValue && (
                                                    <button
                                                        type="button"
                                                        onClick={() => copyToClipboard(copyValue, label)}
                                                        aria-label={`Copy ${label.toLowerCase()}`}
                                                        title={copied === label ? 'Copied' : `Copy ${label.toLowerCase()}`}
                                                        className="flex-shrink-0 p-1.5 -m-1 rounded text-gray-400 hover:text-black hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-black"
                                                    >
                                                        {copied === label ? (
                                                            <svg className="w-4 h-4 text-green-600 animate-pop-in" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                )}
                                            </dd>
                                        </div>
                                    ) : null)}
                                </dl>

                                <div className="h-5" aria-hidden="true">
                                    {copied && (
                                        <p className="text-xs text-green-700 font-light text-right pt-1" role="status">
                                            {copied} copied
                                        </p>
                                    )}
                                </div>

                                {/* Divider makes the two methods alternatives
                                    rather than sequential steps. */}
                                <div className="flex items-center gap-4 my-6">
                                    <span className="h-px flex-grow bg-gray-200" />
                                    <span className="text-xs tracking-widest text-gray-400 font-light">OR SCAN TO PAY</span>
                                    <span className="h-px flex-grow bg-gray-200" />
                                </div>

                                {/* A DuitNow merchant QR is static — it carries the
                                    payee, not the amount — so the reminder below is
                                    load-bearing, not decoration. */}
                                <figure className="text-center">
                                    <img
                                        src="/company-qr.jpeg"
                                        alt="DuitNow QR for TN MEDICA ENTERPRISE — scan with your banking or e-wallet app"
                                        className="w-48 sm:w-56 mx-auto rounded"
                                        loading="lazy"
                                    />
                                    <figcaption className="text-sm text-gray-600 font-light mt-4 max-w-sm mx-auto leading-relaxed">
                                        Scan with any banking or e-wallet app, then enter{' '}
                                        <strong className="text-black">{money(order.totalPrice)}</strong> and reference{' '}
                                        <strong className="text-black">{order.orderNumber}</strong>.
                                    </figcaption>
                                </figure>

                                <div className="border-t border-gray-200 mt-8 pt-6">
                                <a
                                    href={payment.whatsappUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#20bd5a] text-white py-4 font-medium tracking-wide transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.548 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.488" />
                                    </svg>
                                    Send payment receipts
                                </a>

                                {order.status === 'awaiting_payment' && (
                                    <button
                                        onClick={reportPaymentSent}
                                        disabled={reporting}
                                        className="w-full mt-3 border border-gray-300 hover:border-black py-3 font-light text-sm transition-colors disabled:opacity-50"
                                    >
                                        {reporting ? 'Updating…' : "I've sent the payment"}
                                    </button>
                                )}
                                </div>
                            </>
                        ) : (
                            <p className="text-sm font-light text-gray-600 border border-gray-200 p-4">
                                Payment details are not configured yet. Please contact us to complete this order.
                            </p>
                        )}
                    </div>
                )}

                {/* Progress */}
                <div className={`bg-white border p-8 transition-colors duration-500 ${
                    justAdvanced ? 'border-green-500' : 'border-gray-200'
                }`}>
                    <div className="flex items-center justify-between gap-4 mb-6">
                        <h2 className="text-xl font-light tracking-tight">Progress</h2>
                        {!isLive ? null : (
                            <span className="flex items-center gap-2 text-xs text-gray-400 font-light" title="This page updates on its own">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                                </span>
                                Live
                            </span>
                        )}
                    </div>

                    {justAdvanced && (
                        <p className="mb-5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 px-4 py-3 animate-pop-in" role="status">
                            Updated — {(HEADLINES[justAdvanced] || {}).title || 'your order has moved on'}.
                        </p>
                    )}
                    {isCancelled ? (
                        <p className="font-light text-gray-600">This order was cancelled.</p>
                    ) : (
                        <ol className="space-y-4">
                            {STAGES.map((stage, i) => {
                                const done = i < currentStage;
                                const active = i === currentStage;
                                return (
                                    <li key={stage.key} className="flex gap-4">
                                        <div className="flex flex-col items-center">
                                            <span className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${
                                                done ? 'bg-black' : active ? 'bg-black ring-4 ring-gray-200' : 'bg-gray-200'
                                            }`} />
                                            {i < STAGES.length - 1 && <span className={`w-px flex-1 mt-1 ${done ? 'bg-black' : 'bg-gray-200'}`} />}
                                        </div>
                                        <div className="pb-2">
                                            <p className={`font-light ${active ? 'text-black' : done ? 'text-gray-700' : 'text-gray-400'}`}>
                                                {stage.label}
                                            </p>
                                            {active && <p className="text-sm text-gray-500 font-light mt-0.5">{stage.blurb}</p>}
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )}

                    {order.trackingNumber && (
                        <div className="mt-6 pt-6 border-t border-gray-200 text-sm font-light">
                            <div className="flex justify-between gap-4">
                                <span className="text-gray-500">Courier</span><span>{order.courier || '—'}</span>
                            </div>
                            <div className="flex justify-between gap-4 mt-2">
                                <span className="text-gray-500">Tracking number</span>
                                <span className="font-medium break-all">{order.trackingNumber}</span>
                            </div>
                        </div>
                    )}

                    {order.receiptUrl && (
                        <a href={order.receiptUrl} target="_blank" rel="noopener noreferrer"
                            className="inline-block mt-6 underline font-light text-sm">
                            View receipt {order.receiptNumber}
                        </a>
                    )}
                </div>

                {/* Items */}
                <div className="bg-white border border-gray-200 p-8">
                    <h2 className="text-xl font-light tracking-tight mb-6">Items</h2>
                    <ul className="space-y-4">
                        {(order.items || []).map((item, i) => (
                            <li key={i} className="flex items-center gap-4">
                                {item.productId?.image && (
                                    <img src={resolveImageUrl(item.productId.image)} alt=""
                                        className="w-14 h-14 object-cover rounded flex-shrink-0" />
                                )}
                                <div className="flex-grow min-w-0">
                                    <p className="font-light truncate">{item.productId?.name || 'Item'}</p>
                                    {item.variantLabel && (
                                        <p className="text-xs text-gray-500 font-light">{item.variantLabel}</p>
                                    )}
                                    <p className="text-sm text-gray-500 font-light">Qty {item.quantity}</p>
                                </div>
                                <span className="font-light whitespace-nowrap">{money(item.price * item.quantity)}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="flex justify-between pt-6 mt-6 border-t border-gray-200 text-lg">
                        <span className="font-light">Total</span>
                        <span className="font-medium">{money(order.totalPrice)}</span>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default OrderStatusPage;
